import { useMemo } from 'react';
import { UserWallet } from '../services/walletService';
import { FinancialStatus, calculateFinancialStatus } from '../services/walletService';

interface UseFinancialStatusReturn extends FinancialStatus {
  isCheckingBalance: boolean;
}

/**
 * Hook para verificar el estado financiero del usuario
 * Implementa la lógica del "semáforo" para determinar si puede enviar mensajes
 * 
 * Lógica:
 * 1. ¿subscription_balance > 0? → ✅ Luz verde
 * 2. Si no, ¿current_overage_spend < overage_limit? → ✅ Luz verde (usando overage)
 * 3. Si ninguna de las dos → ⛔ Luz roja
 * 
 * @param wallet - Datos de la wallet del usuario (de useWallet)
 * @param isLoadingWallet - Si está cargando los datos de la wallet
 */
export function useFinancialStatus(
  wallet: UserWallet | null, 
  isLoadingWallet: boolean
): UseFinancialStatusReturn {
  
  const financialStatus = useMemo(() => {
    return calculateFinancialStatus(wallet);
  }, [wallet]);

  return {
    ...financialStatus,
    isCheckingBalance: isLoadingWallet
  };
}

/**
 * Mensajes de error según la razón del bloqueo
 */
export function getInsufficientFundsMessage(reason: FinancialStatus['reason']): {
  title: string;
  message: string;
  action: string;
} {
  switch (reason) {
    case 'no_wallet':
      return {
        title: 'Account not configured',
        message: 'No account information found. Please contact support.',
        action: 'Contact support'
      };
    case 'insufficient_funds':
      return {
        title: 'Insufficient balance',
        message: 'You have reached your subscription balance, promo balance and credit limit. Recharge to continue using the assistant.',
        action: 'Recharge'
      };
    default:
      return {
        title: 'Error',
        message: 'An error occurred while verifying your balance.',
        action: 'Retry'
      };
  }
}
